import { useMemo, type FC } from "react";
import { useParams } from "react-router-dom";
import { Heading2 } from "@/components/podkit/typography/Headings";
import { useGetWorkflow, useGetWorkflowExecutionAction } from "@/queries/automation-queries";
import { ErrorMessage } from "@/components/ErrorMessage";
import { WorkflowExecutionActionStepList } from "@/components/automations/AutomationExecutionActionStepList";
import { getProjectIDFromContext, workflowName } from "@/utils/automations";
import { Banner } from "@/components/flexkit/Banner";
import { useProject } from "@/queries/project-queries";
import { WorkflowExecutionActionDropdownMenu } from "@/components/automations/AutomationExecutionActionDropdownMenu";
import { useGetAgentExecution } from "@/queries/agent-queries";
import { useStreamConversation } from "@/hooks/use-stream-conversation";
import { ChatMessages } from "@/components/ai/chat/ChatMessages";
import { WorkflowExecutionAction_Status_StepStatus_StepPhase } from "gitpod-next-api/gitpod/v1/workflow_pb";
import { IconCircleBanSign } from "central-icons/IconCircleBanSign";
import { IconCircleCheck } from "central-icons/IconCircleCheck";
import { IconCircleDotted } from "central-icons/IconCircleDotted";
import { IconTriangleExclamation } from "central-icons/IconTriangleExclamation";
import { Text } from "@/components/podkit/typography/Text";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { hasStepFailed, isActionCancelling } from "@/utils/automation-execution-action";

export const AutomationExecutionActionPage: FC = () => {
    const { workflowExecutionActionId } = useParams<{
        workflowExecutionActionId: string;
    }>();
    const { data: workflowExecutionAction, error: workflowExecutionActionError } =
        useGetWorkflowExecutionAction(workflowExecutionActionId);
    const { data: workflow } = useGetWorkflow(workflowExecutionAction?.metadata?.workflowId);

    const projectId = getProjectIDFromContext(workflowExecutionAction?.spec?.context);
    const { data: project } = useProject(projectId ?? undefined);

    const { data: agentExecution } = useGetAgentExecution(workflowExecutionAction?.status?.agentExecutionId);
    const { messages } = useStreamConversation(
        agentExecution?.id,
        agentExecution?.status?.conversationUrl,
        [], // no pending messages as the conversation is driven by the workflow
    );

    const isCancelling = workflowExecutionAction ? isActionCancelling(workflowExecutionAction) : false;

    const counters = useMemo(() => {
        let completed = 0;
        let failed = 0;
        let cancelled = 0;
        let incomplete = 0;
        let cancelling = 0;
        workflowExecutionAction?.status?.stepStatuses.forEach((step) => {
            if (hasStepFailed(step)) {
                failed++;
            } else {
                switch (step.phase) {
                    case WorkflowExecutionAction_Status_StepStatus_StepPhase.DONE:
                        completed++;
                        break;
                    case WorkflowExecutionAction_Status_StepStatus_StepPhase.FAILED:
                        failed++;
                        break;
                    case WorkflowExecutionAction_Status_StepStatus_StepPhase.CANCELLED:
                        cancelled++;
                        break;
                    default:
                        // When action is cancelling, count pending/running steps as cancelling
                        if (isCancelling) {
                            cancelling++;
                        } else {
                            incomplete++;
                        }
                }
            }
        });
        return { completed, failed, cancelled, incomplete, cancelling };
    }, [workflowExecutionAction, isCancelling]);

    return (
        <div data-testid="workflow-execution-action-page" className="flex size-full flex-col gap-4">
            <ErrorMessage error={workflowExecutionActionError} />

            {/* HEADER */}
            <div className="flex items-start justify-between gap-2">
                <div className="flex flex-col gap-1">
                    <Heading2 className="text-xl font-semibold leading-tight">
                        &quot;{workflowExecutionAction?.metadata?.actionName ?? <span>-</span>}
                        <span>&quot; Action Report</span>
                    </Heading2>
                    <Text className="text-sm text-content-secondary">
                        {workflowName(workflow)} / {project?.metadata?.name}
                    </Text>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex gap-3">
                        {counters.completed > 0 && (
                            <div className="flex items-center gap-1">
                                <IconCircleCheck size={16} className="text-content-success" />
                                <Text className="text-sm text-content-success">{counters.completed} completed</Text>
                            </div>
                        )}
                        {counters.failed > 0 && (
                            <div className="flex items-center gap-1">
                                <IconTriangleExclamation size={16} className="text-content-destructive" />
                                <Text className="text-sm text-content-destructive">{counters.failed} failed</Text>
                            </div>
                        )}
                        {counters.cancelled > 0 && (
                            <div className="flex items-center gap-1">
                                <IconCircleBanSign size={16} className="text-content-muted" />
                                <Text className="text-sm text-content-muted">{counters.cancelled} cancelled</Text>
                            </div>
                        )}
                        {counters.cancelling > 0 && (
                            <div className="flex items-center gap-1">
                                <IconCircleDotted size={16} className="text-content-muted" />
                                <Text className="text-sm text-content-muted">{counters.cancelling} cancelling</Text>
                            </div>
                        )}
                        {counters.incomplete > 0 && (
                            <div className="flex items-center gap-1">
                                <IconCircleDotted size={16} className="text-content-muted" />
                                <Text className="text-sm text-content-muted">{counters.incomplete} incomplete</Text>
                            </div>
                        )}
                    </div>
                    {workflowExecutionAction && (
                        <WorkflowExecutionActionDropdownMenu action={workflowExecutionAction} />
                    )}
                </div>
            </div>

            {workflowExecutionAction?.status?.failureMessage && (
                <Banner variant="warning" text={workflowExecutionAction.status.failureMessage} />
            )}

            {/* CONTENT */}
            <ScrollArea
                orientation="both"
                id="workflow-execution-action-conversation-content"
                className="max-h-full flex-grow"
            >
                {workflowExecutionAction && (
                    <div className="flex flex-col gap-4">
                        <WorkflowExecutionActionStepList action={workflowExecutionAction} />
                        {agentExecution && (
                            <>
                                <hr className="border-border-subtle" />
                                <div className="flex flex-col gap-2">
                                    <Text className="text-base font-normal text-content-primary">
                                        Full Conversation
                                    </Text>
                                    <ChatMessages
                                        agentExecution={agentExecution}
                                        messages={messages}
                                        enableTips={false}
                                        className="h-auto max-w-none grow self-stretch rounded-lg border border-border-base bg-surface-base p-4"
                                    />
                                </div>
                            </>
                        )}
                    </div>
                )}
            </ScrollArea>
        </div>
    );
};
